// Quiz content for "Your Working Life: What Do You Want More Of?"
// Scoring letters are internal only and never shown to visitors.
// A = Freedom, B = Meaning, C = Growth, D = Connection, E = Balance and energy

export const CATEGORY_ORDER = ["freedom", "meaning", "growth", "connection", "balance"];

export const CATEGORY_LABEL = {
  freedom: "Freedom",
  meaning: "Meaning",
  growth: "Growth",
  connection: "Connection",
  balance: "Balance and energy",
};

export const CATEGORY_COLOR = {
  freedom: "#4aa0a4",
  meaning: "#e93e36",
  growth: "#f4b847",
  connection: "#3b868a",
  balance: "#d97724",
};

const L = ["A", "B", "C", "D", "E"];

export const QUESTIONS = [
  {
    prompt: "Imagine finishing a really satisfying working day. What made it good?",
    options: [
      "I had the freedom to decide how to spend my time.",
      "I worked on something that mattered to me.",
      "I learned something and stretched myself.",
      "I shared ideas with people who valued my contribution.",
      "I still had energy for the rest of my life.",
    ],
  },
  {
    prompt: "What would you most like to make more room for?",
    options: [
      "Working in a way that suits me.",
      "Putting my experience towards a worthwhile purpose.",
      "Exploring ideas I\u2019ve kept on the back burner.",
      "Honest conversations and shared effort.",
      "Time to think, rest and enjoy life.",
    ],
  },
  {
    prompt: "Which opportunity would you be most tempted to say yes to?",
    options: [
      "Taking ownership of a project and shaping how it runs.",
      "Helping solve a problem I care deeply about.",
      "Trying something unfamiliar with support to learn.",
      "Joining a group where people help each other progress.",
      "Doing rewarding work within clear, manageable boundaries.",
    ],
  },
  {
    prompt: "Which sentence best captures something you miss?",
    options: [
      "\u201CI wish I had more say in how I work.\u201D",
      "\u201CI wish I could see why my work matters.\u201D",
      "\u201CI wish I felt curious and challenged again.\u201D",
      "\u201CI wish I had people I could think things through with.\u201D",
      "\u201CI wish work left me with more energy.\u201D",
    ],
  },
  {
    prompt: "If you could change one thing about your working week, what would it be?",
    options: [
      "More choice over my schedule and priorities.",
      "More time spent on work aligned with my values.",
      "More space to experiment, create and develop skills.",
      "More collaboration with people I trust.",
      "A pace I could comfortably sustain.",
    ],
  },
  {
    prompt: "What would make your experience feel more valuable?",
    options: [
      "Being trusted to use my judgement.",
      "Seeing it make a useful difference.",
      "Combining it with fresh ideas and new knowledge.",
      "Sharing it with people who also teach me something.",
      "Using it well without having to give all of myself.",
    ],
  },
  {
    prompt: "When you picture your next chapter, what excites you most?",
    options: [
      "Having greater ownership of my working life.",
      "Building something I believe in.",
      "Discovering what else I\u2019m capable of.",
      "Finding a community where I can contribute and belong.",
      "Making work fit the life I want to lead.",
    ],
  },
  {
    prompt: "A year from now, which statement would you most like to make?",
    options: [
      "\u201CI have more freedom to choose.\u201D",
      "\u201CMy work feels meaningful.\u201D",
      "\u201CI\u2019m learning, creating and moving forward.\u201D",
      "\u201CI\u2019ve found my people.\u201D",
      "\u201CI have energy for what matters.\u201D",
    ],
  },
].map((q) => ({
  ...q,
  options: q.options.map((text, i) => ({ letter: L[i], text })),
}));

export const RESULTS = {
  freedom: {
    heading: "More freedom",
    body: "You may be looking for greater say over your time, decisions and working methods. Start by identifying where more choice would make the biggest difference.",
    reflection: "Where do I want more freedom\u2014and what responsibilities am I willing to take on with it?",
    nextStep: "Choose one part of your working week you would like to reshape. Explore a practical way to test that change.",
  },
  meaning: {
    heading: "More meaning",
    body: "You may want a clearer connection between your work, your values and the difference you make. Your experience could have a valuable new purpose.",
    reflection: "Whose problem would I feel proud to help solve?",
    nextStep: "Speak to someone facing a challenge you care about. Listen for where your experience might help.",
  },
  growth: {
    heading: "More growth",
    body: "You may be ready for renewed curiosity, creativity and challenge. Becoming a beginner again can open possibilities you cannot yet see.",
    reflection: "What would I explore if I didn\u2019t need to be good at it immediately?",
    nextStep: "Give yourself an hour to try something you\u2019ve been postponing. Notice what holds your interest.",
  },
  connection: {
    heading: "More connection",
    body: "You may want people to exchange ideas with, encouragement you can trust and a stronger sense of belonging.",
    reflection: "Who helps me think clearly and feel able to contribute?",
    nextStep: "Arrange a conversation with someone you can be open with, or join a group built around a shared interest.",
  },
  balance: {
    heading: "More balance and energy",
    body: "You may want a working life that leaves room for relationships, health and enjoyment. Consider what a sustainable version of success would look like for you.",
    reflection: "What do I want my work to make possible beyond work itself?",
    nextStep: "Identify one recurring demand you could reduce, renegotiate or stop. Use the space for something that restores you.",
  },
};

export const CONSENT = {
  yes: {
    title: "Yes, keep me informed",
    detail: "Email me occasional insights, events and opportunities from The Weave, including the Founder Academy.",
  },
  no: {
    title: "No, thank you",
    detail: "Just send my quiz results and the Personal Clarity Prospectus.",
  },
};

export const LOGO_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_working-life-quiz/artifacts/qpbkxg5q_WEAVE-LOGO%202024.webp";
